/*
 * Interactive demos for the "See it in action" patterns, one per pattern id.
 *
 * FAITHFUL PORT of the `ai-ui-patterns` handoff's `D` object: each demo is a
 * markup string plus optional `init` (wire up interactivity) and `run` (play an
 * animation). `PatternDemo` drives them — it drops the markup into a detached box
 * and calls init/run, exactly as the handoff did. The logic is kept close to the
 * source on purpose: these animations were hand-tuned, and a rewrite would be a
 * chance to introduce timing/behaviour drift. That is why this module is
 * imperative DOM rather than idiomatic JSX.
 *
 * PALETTE: the demos keep the handoff's own colours (`--ai` purple, `--ok` green,
 * etc.), which are NOT this app's warm theme. They are scoped to the demo stage in
 * `PatternDemo.module.css` and defined nowhere else — a deliberate contained
 * exception, like the AWS brand hues in `content/techStack.ts`.
 *
 * FIVE demos are ORIGINAL, not from the handoff — it predates these patterns:
 *   mixed · readaloud · monitoring · planreview · assumptions
 * They are written in the same vocabulary and are flagged below with NEW. They
 * are draft, for review, same footing as those patterns' copy in `patterns.ts`.
 */

// Shared with the rest of the app's reduced-motion handling: skip the waits so
// the demos jump to their resting state rather than animating.
const RM =
  typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, RM ? 0 : ms))

/**
 * Type `text` into `el` one character at a time, bailing if `root` is torn off
 * the document (a replay or a closed overlay). Returns whether it finished.
 */
async function typeText(
  el: HTMLElement,
  text: string,
  root: Element,
  speed = 26
): Promise<boolean> {
  for (const ch of text) {
    if (!root.isConnected) return false
    el.textContent = (el.textContent ?? "") + ch
    await wait(speed)
  }
  return root.isConnected
}

// The handoff's `$` / `$$`. Typed non-null for the common element case so the
// ported demo bodies stay verbatim; SVG lookups pass their own element type.
const $ = <T extends Element = HTMLElement>(sel: string, root: ParentNode): T =>
  root.querySelector(sel) as T
const $$ = <T extends Element = HTMLElement>(sel: string, root: ParentNode): T[] =>
  Array.from(root.querySelectorAll<Element>(sel)) as T[]

export interface Demo {
  html: string
  /** Show a "Try it" badge (interactive demos with no auto-run). */
  try?: boolean
  /** Wire up interactivity once the markup is in the DOM. */
  init?: (r: HTMLElement) => void
  /** Play the animation; started when the stage scrolls into view. */
  run?: (r: HTMLElement) => Promise<void> | void
}

export const DEMOS: Record<string, Demo> = {
  /* ---------------------------------------------------- ported from handoff */

  stream: {
    html: `<div class="d col">
      <div class="bub me">Write a tagline for a running app</div>
      <div class="bub ai"><span class="t"></span><span class="caret"></span></div>
      <div class="acts"><button class="mini">Stop</button></div></div>`,
    async run(r) {
      const ok = await typeText(
        $(".t", r),
        "Every mile, a little more you. Built for the runs you almost skipped.",
        r,
        30
      )
      if (!ok) return
      $(".caret", r).remove()
      $(".acts", r).innerHTML =
        '<button class="mini fade">Copy</button><button class="mini fade">Regenerate</button>'
    },
  },

  starters: {
    try: true,
    html: `<div class="d col" style="align-items:center;text-align:center">
      <div style="font-size:16px;font-weight:600">What can I help with?</div>
      <div class="acts" style="justify-content:center">
        <button class="chip">Plan a sprint review</button><button class="chip">Summarize this week</button><button class="chip">Draft release notes</button></div>
      <div class="field" style="width:100%;text-align:left"><span class="in ph">Ask anything</span></div></div>`,
    init(r) {
      $$(".chip", r).forEach(
        (c) =>
          (c.onclick = () => {
            const i = $(".in", r)
            i.textContent = c.textContent
            i.classList.remove("ph")
          })
      )
    },
  },

  ghost: {
    try: true,
    html: `<div class="d col">
      <div class="field" tabindex="0" style="min-height:76px"><span class="typed"></span><span class="ghost"></span></div>
      <div class="lbl hint" style="visibility:hidden"><kbd>Tab</kbd> or click to accept</div></div>`,
    async run(r) {
      const ok = await typeText(
        $(".typed", r),
        "Thanks for the update. I'll review the deck",
        r,
        35
      )
      if (!ok) return
      await wait(300)
      $(".ghost", r).textContent = " and send notes by Friday."
      $(".hint", r).style.visibility = "visible"
    },
    init(r) {
      const accept = () => {
        const g = $(".ghost", r)
        if (!g.textContent) return
        const s = document.createElement("span")
        s.className = "accepted"
        s.textContent = g.textContent
        $(".typed", r).after(s)
        g.textContent = ""
        $(".hint", r).style.visibility = "hidden"
      }
      const f = $(".field", r)
      f.addEventListener("keydown", (e) => {
        if (e.key === "Tab" && $(".ghost", r).textContent) {
          e.preventDefault()
          accept()
        }
      })
      $(".ghost", r).onclick = accept
    },
  },

  selbar: {
    try: true,
    html: `<div class="d col">
      <div class="panel acts" style="align-self:flex-start;padding:4px;gap:2px;border-radius:10px">
        <button class="mini" data-v="will launch the feature" style="border:0">Shorten</button>
        <button class="mini" data-v="is excited to launch the feature" style="border:0">Friendlier</button>
        <button class="mini" data-v="ha decidido lanzar la función" style="border:0">Translate</button></div>
      <p style="margin:0;font-size:14px">Our team <span class="sel">has made the decision to move forward with launching the feature</span> next week.</p></div>`,
    init(r) {
      $$("[data-v]", r).forEach(
        (b) =>
          (b.onclick = () => {
            const s = $(".sel,.tint", r)
            s.className = "tint fade"
            s.textContent = b.dataset.v ?? ""
          })
      )
    },
  },

  variants: {
    try: true,
    html: `<div class="d col"><div class="lbl">Subject line, pick one</div>
      ${["Your March report is ready", "3 things that changed in March", "March, in one minute"]
        .map(
          (t) =>
            `<button class="ai-box opt" style="text-align:left;display:flex;justify-content:space-between;border:1.5px solid transparent">${t}<span class="check" hidden>✓</span></button>`
        )
        .join("")}</div>`,
    init(r) {
      $$(".opt", r).forEach(
        (o) =>
          (o.onclick = () => {
            $$(".opt", r).forEach((x) => {
              x.style.borderColor = "transparent"
              $(".check", x).hidden = true
            })
            o.style.borderColor = "var(--ink)"
            $(".check", o).hidden = false
          })
      )
    },
  },

  tldr: {
    html: `<div class="d col">
      <button class="ai-box head" style="display:flex;justify-content:space-between;font-weight:600;border-radius:10px 10px 0 0"><span>Summary</span><span class="arrow">Show</span></button>
      <ul class="ai-box sum" style="margin:-8px 0 0;padding:4px 12px 10px 28px;border-radius:0 0 10px 10px;display:none"></ul>
      <div class="sk" style="width:92%"></div><div class="sk" style="width:80%"></div><div class="sk" style="width:86%"></div><div class="sk" style="width:60%"></div></div>`,
    async run(r) {
      const ul = $(".sum", r)
      const items = [
        "Launch moved to May 12",
        "Onboarding design needs two more weeks",
        "Budget approved, no changes",
      ]
      await wait(500)
      if (!r.isConnected) return
      ul.style.display = "block"
      $(".arrow", r).textContent = "Hide"
      for (const t of items) {
        if (!r.isConnected) return
        const li = document.createElement("li")
        li.className = "fade"
        li.textContent = t
        ul.append(li)
        await wait(350)
      }
    },
    init(r) {
      $(".head", r).onclick = () => {
        const u = $(".sum", r)
        const open = u.style.display !== "none"
        u.style.display = open ? "none" : "block"
        $(".arrow", r).textContent = open ? "Show" : "Hide"
      }
    },
  },

  regen: {
    try: true,
    html: `<div class="d col">
      <div class="bub ai t" style="max-width:100%;min-height:58px"></div>
      <div class="acts"><button class="mini prev" aria-label="Previous version">‹</button><span class="ver lbl"></span><button class="mini next" aria-label="Next version">›</button><button class="mini regen">Regenerate</button></div></div>`,
    init(r) {
      const v = [
        "Welcome aboard. Here's what to set up first.",
        "Glad you're here. Three quick steps to get going.",
      ]
      let i = 1
      const show = () => {
        $(".t", r).textContent = v[i]
        $(".ver", r).textContent = `${i + 1} / ${v.length}`
      }
      $(".prev", r).onclick = () => {
        i = Math.max(0, i - 1)
        show()
      }
      $(".next", r).onclick = () => {
        i = Math.min(v.length - 1, i + 1)
        show()
      }
      $(".regen", r).onclick = async () => {
        if (v.length >= 4) return
        $(".t", r).textContent = "…"
        await wait(500)
        v.push(
          [
            "You're in. Let's make your first project together.",
            "Hi there. Start with your profile, then invite your team.",
          ][v.length - 2]
        )
        i = v.length - 1
        show()
      }
      show()
    },
  },

  cite: {
    try: true,
    html: `<div class="d col">
      <div class="ai-box">Q3 revenue grew 12%<button class="mini c" data-s="Q3 financial report, page 4" style="padding:0 6px;margin:0 2px;font-size:10px;vertical-align:2px">1</button>, mostly from enterprise renewals<button class="mini c" data-s="Sales review deck, slide 9" style="padding:0 6px;margin:0 2px;font-size:10px;vertical-align:2px">2</button>.</div>
      <div class="panel src lbl" style="font-size:12px">Select a number to see its source</div></div>`,
    init(r) {
      $$(".c", r).forEach(
        (c) =>
          (c.onclick = () => {
            $$(".c", r).forEach((x) => (x.style.borderColor = ""))
            c.style.borderColor = "var(--ai)"
            const s = $(".src", r)
            s.innerHTML = `<div style="color:var(--ink);font-weight:600">${c.dataset.s}</div><div class="sk" style="width:90%;margin-top:6px"></div><div class="sk" style="width:70%;margin-top:4px;background:var(--ai-tint)"></div>`
          })
      )
    },
  },

  tags: {
    try: true,
    html: `<div class="d"><div class="panel col">
      <div class="lbl">Acme Billing</div><div style="font-weight:600">Invoice #4821 is 15 days overdue</div>
      <div class="acts">${["Billing", "Urgent"]
        .map(
          (t) =>
            `<span class="tg" style="display:inline-flex;align-items:center;gap:4px;border:1px dashed var(--ai);background:var(--ai-tint);border-radius:999px;padding:2px 4px 2px 10px;font-size:12px">${t}<button class="ok" aria-label="Accept ${t}" style="padding:0 4px">✓</button><button class="no" aria-label="Remove ${t}" style="padding:0 4px;color:var(--muted)">✕</button></span>`
        )
        .join("")}<span class="lbl">Suggested</span></div></div></div>`,
    init(r) {
      $$(".tg", r).forEach((t) => {
        $(".ok", t).onclick = () => {
          t.style.border = "1px solid var(--ink)"
          t.style.background = "#fff"
          t.style.paddingRight = "10px"
          $$("button", t).forEach((b) => b.remove())
        }
        $(".no", t).onclick = () => t.remove()
      })
    },
  },

  autofill: {
    html: `<div class="d row" style="align-items:stretch;max-width:320px">
      <div class="panel col" style="width:42%;gap:6px;font-size:11px"><b>Blue Bottle</b><div class="sk"></div><div class="sk" style="width:70%"></div><span>Mar 4, 2026</span><div class="sk" style="width:80%"></div><b>Total $18.40</b></div>
      <div class="col" style="flex:1;gap:6px">${["Merchant", "Date", "Amount"]
        .map(
          (l) =>
            `<div><div class="lbl">${l}</div><div class="field f" style="min-height:28px;padding:5px 9px"></div></div>`
        )
        .join(
          ""
        )}<div class="lbl note" style="visibility:hidden">Filled from receipt. Check before saving.</div></div></div>`,
    async run(r) {
      const v = ["Blue Bottle", "Mar 4, 2026", "$18.40"]
      const f = $$(".f", r)
      await wait(400)
      for (let i = 0; i < 3; i++) {
        if (!r.isConnected) return
        f[i].style.background = "var(--ai-tint)"
        f[i].style.borderColor = "transparent"
        f[i].textContent = v[i]
        f[i].classList.add("fade")
        await wait(450)
      }
      if (r.isConnected) $(".note", r).style.visibility = "visible"
    },
  },

  answer: {
    html: `<div class="d col">
      <div class="field"><span class="q"></span></div>
      <div class="ai-box ans" style="visibility:hidden"><b>25 days a year</b>, plus public holidays where you work.<div class="lbl" style="margin-top:4px">From: Time-off policy</div></div>
      <div class="res col" style="gap:5px;visibility:hidden"><div class="sk" style="width:60%;background:#cfcfd6"></div><div class="sk" style="width:90%"></div><div class="sk" style="width:55%;background:#cfcfd6;margin-top:4px"></div><div class="sk" style="width:84%"></div></div></div>`,
    async run(r) {
      if (!(await typeText($(".q", r), "how many vacation days do I get?", r, 32))) return
      await wait(400)
      if (!r.isConnected) return
      $(".ans", r).style.visibility = "visible"
      $(".ans", r).classList.add("fade")
      await wait(300)
      $(".res", r).style.visibility = "visible"
    },
  },

  because: {
    html: `<div class="d col"><div style="font-weight:600">Because you saved “Ceramics 101”</div>
      <div class="row">${[
        ["Glazing basics", "#d8cfc4"],
        ["Wheel throwing", "#c9d3cf"],
        ["Kiln safety", "#d6d0dc"],
      ]
        .map(
          ([t, c]) =>
            `<div style="flex:1"><div style="height:64px;border-radius:10px;background:${c}"></div><div style="font-size:12px;margin-top:4px">${t}</div></div>`
        )
        .join("")}</div></div>`,
  },

  anomaly: {
    html: `<div class="d col"><div class="panel">
      <div class="lbl">Daily sign-ups</div>
      <svg viewBox="0 0 280 80" width="100%" aria-hidden="true"><polyline class="ln" fill="none" stroke="var(--ink)" stroke-width="2" stroke-linejoin="round"/><circle class="dot" r="9" fill="none" stroke="var(--ai)" stroke-width="2" opacity="0"/></svg></div>
      <div class="ai-box co" style="visibility:hidden">Sign-ups fell 38% on Tuesday. Checkout errors spiked at the same time.</div></div>`,
    async run(r) {
      const v = [40, 43, 45, 44, 48, 50, 29, 46, 49, 52]
      const pts = v.map((y, i) => [10 + i * 29, 92 - y * 1.5])
      const ln = $<SVGPolylineElement>(".ln", r)
      ln.setAttribute("points", pts.map((p) => p.join(",")).join(" "))
      const len = ln.getTotalLength ? ln.getTotalLength() : 600
      ln.style.strokeDasharray = String(len)
      ln.style.strokeDashoffset = String(len)
      ln.style.transition = RM ? "none" : "stroke-dashoffset 1s ease"
      await wait(50)
      ln.style.strokeDashoffset = "0"
      await wait(1000)
      if (!r.isConnected) return
      const d = $<SVGCircleElement>(".dot", r)
      d.setAttribute("cx", String(pts[6][0]))
      d.setAttribute("cy", String(pts[6][1]))
      d.setAttribute("opacity", "1")
      await wait(300)
      const c = $(".co", r)
      c.style.visibility = "visible"
      c.classList.add("fade")
    },
  },

  nudge: {
    try: true,
    html: `<div class="d col">
      <div class="ai-box nb" style="visibility:hidden"><div>3 tickets describe the same login bug.</div><div class="acts" style="margin-top:8px"><button class="btn primary m">Merge tickets</button><button class="btn x">Dismiss</button></div></div>
      ${["#212 Can't sign in on Safari", "#215 Login loops back", "#218 Stuck after password"]
        .map((t) => `<div class="panel tk" style="padding:8px 10px;font-size:12px">${t}</div>`)
        .join("")}</div>`,
    async run(r) {
      await wait(600)
      if (!r.isConnected) return
      const b = $(".nb", r)
      b.style.visibility = "visible"
      b.classList.add("fade")
    },
    init(r) {
      $(".m", r).onclick = () => {
        $$(".tk", r)
          .slice(1)
          .forEach((t) => t.remove())
        $(".nb", r).innerHTML = 'Merged into #212. <button class="mini">Undo</button>'
      }
      $(".x", r).onclick = () => ($(".nb", r).style.visibility = "hidden")
    },
  },

  plan: {
    html: `<div class="d"><div class="panel col">
      <div style="font-weight:600">Sending payment reminders</div>
      ${["Find overdue invoices", "Draft 12 reminder emails", "Schedule for Monday 9 am"]
        .map(
          (t) =>
            `<div class="row st" style="color:var(--muted)"><span class="ic" style="width:16px;text-align:center">○</span>${t}</div>`
        )
        .join("")}</div></div>`,
    async run(r) {
      for (const s of $$(".st", r)) {
        if (!r.isConnected) return
        s.style.color = "var(--ink)"
        $(".ic", s).textContent = "◌"
        $(".ic", s).style.color = "var(--ai)"
        await wait(900)
        if (!r.isConnected) return
        $(".ic", s).textContent = "✓"
        $(".ic", s).style.color = "var(--ok)"
      }
    },
  },

  approve: {
    try: true,
    html: `<div class="d"><div class="panel col">
      <div style="font-weight:600">Ready to send 12 reminder emails</div><div class="lbl">To overdue accounts, from billing@loka.com</div>
      <div class="ai-box" style="font-size:12px">Hi Dana, a quick reminder that invoice #4821 is now 15 days overdue…</div>
      <div class="acts out"><button class="btn">Edit emails</button><button class="btn primary go">Send emails</button></div></div></div>`,
    init(r) {
      $(".go", r).onclick = () => {
        $(".out", r).innerHTML = '<span class="check fade">✓ Sent 12 emails</span>'
      }
    },
  },

  clarify: {
    try: true,
    html: `<div class="d col">
      <div class="bub me">Book a room for the design review</div>
      <div class="bub ai">Sure. Which day works?</div>
      <div class="acts ch"><button class="chip">Today</button><button class="chip">Tomorrow</button><button class="chip">Pick a date</button></div></div>`,
    init(r) {
      $$(".ch .chip", r).forEach(
        (c) =>
          (c.onclick = async () => {
            const label = c.textContent ?? ""
            const box = $(".ch", r)
            box.outerHTML = `<div class="bub me fade">${label}</div>`
            await wait(400)
            const a = document.createElement("div")
            a.className = "bub ai fade"
            a.textContent = `Booked Room 4 for ${
              label.toLowerCase() === "pick a date" ? "Thursday" : label.toLowerCase()
            }, 2 to 3 pm.`
            const host = $(".d", r) || r
            host.append(a)
          })
      )
    },
  },

  copilot: {
    html: `<div class="d row" style="max-width:330px;align-items:stretch;height:170px">
      <div class="col" style="flex:1;gap:6px;padding-top:6px"><div style="font-weight:600;font-size:12px">Q3 plan</div><div class="sk"></div><div class="sk" style="width:80%"></div><div class="sk" style="width:90%"></div><div class="sk" style="width:60%"></div></div>
      <div class="panel col" style="width:58%;gap:6px;padding:8px"><div class="lbl">Assistant · using Q3 plan</div>
      <div class="bub me" style="font-size:12px">What are the risks?</div><div class="bub ai a" style="font-size:12px;visibility:hidden">Two: hiring delays and the API migration.</div>
      <div class="field" style="margin-top:auto;min-height:26px;padding:4px 8px"><span class="ph" style="font-size:11px">Ask about this doc</span></div></div></div>`,
    async run(r) {
      await wait(700)
      if (!r.isConnected) return
      const a = $(".a", r)
      a.style.visibility = "visible"
      a.classList.add("fade")
    },
  },

  voice: {
    html: `<div class="d col" style="align-items:center;text-align:center">
      <div style="position:relative;width:52px;height:52px"><span class="ring" style="position:absolute;inset:0;border-radius:50%;background:var(--ai-tint)"></span><span style="position:absolute;inset:8px;border-radius:50%;background:var(--ai);display:grid;place-items:center;color:#fff;font-size:16px">●</span></div>
      <div class="tr" style="font-size:15px;min-height:44px"></div><div class="lbl st">Listening…</div></div>`,
    async run(r) {
      const w = "Remind me to call Ana after the standup tomorrow".split(" ")
      const tr = $(".tr", r)
      for (let i = 0; i < w.length; i++) {
        if (!r.isConnected) return
        tr.innerHTML = w.slice(0, i).join(" ") + ` <span style="color:var(--muted)">${w[i]}</span>`
        await wait(260)
      }
      tr.textContent = w.join(" ")
      $(".st", r).textContent = "Done"
      $(".ring", r).style.animation = "none"
    },
  },

  detect: {
    html: `<div class="d" style="max-width:320px"><svg viewBox="0 0 280 150" width="100%" aria-label="Room photo with detected objects">
      <rect width="280" height="150" rx="10" fill="#e9e6e1"/><rect y="128" width="280" height="22" fill="#d9d4cc"/>
      <rect x="44" y="48" width="8" height="52" fill="#8a7a68"/><rect x="44" y="88" width="48" height="10" fill="#8a7a68"/><rect x="48" y="98" width="5" height="30" fill="#8a7a68"/><rect x="84" y="98" width="5" height="30" fill="#8a7a68"/>
      <rect x="150" y="100" width="30" height="28" fill="#b98b6b"/><circle cx="158" cy="86" r="14" fill="#7d9a7b"/><circle cx="172" cy="80" r="16" fill="#6c8a6a"/>
      <rect x="236" y="48" width="4" height="80" fill="#555"/><polygon points="224,48 252,48 246,30 230,30" fill="#e0c36a"/>
      <g class="bx"></g></svg></div>`,
    async run(r) {
      const g = $<SVGGElement>(".bx", r)
      const b: [string, number, number, number, number][] = [
        ["Chair", 38, 40, 60, 92],
        ["Plant", 138, 60, 48, 70],
        ["Lamp", 218, 24, 40, 106],
      ]
      for (const [t, x, y, w, h] of b) {
        if (!r.isConnected) return
        g.insertAdjacentHTML(
          "beforeend",
          `<g class="fade"><rect x="${x}" y="${y}" width="${w}" height="${h}" rx="4" fill="none" stroke="#6b4eff" stroke-width="2"/><rect x="${x}" y="${y - 14}" width="${t.length * 6 + 10}" height="14" rx="3" fill="#6b4eff"/><text x="${x + 5}" y="${y - 4}" font-size="10" fill="#fff" font-family="-apple-system,system-ui,sans-serif">${t}</text></g>`
        )
        await wait(450)
      }
    },
  },

  translate: {
    try: true,
    html: `<div class="d"><div class="panel col">
      <div class="row"><span style="width:24px;height:24px;border-radius:50%;background:#d6d0dc;display:grid;place-items:center;font-size:11px">L</span><b>Lucía</b></div>
      <div class="txt">Me encanta la nueva navegación, pero el botón de guardar es difícil de encontrar.</div>
      <div class="row"><button class="mini tg">Show translation</button><span class="lbl info"></span></div></div></div>`,
    init(r) {
      let on = false
      const es = "Me encanta la nueva navegación, pero el botón de guardar es difícil de encontrar."
      const en = "I love the new navigation, but the save button is hard to find."
      $(".tg", r).onclick = () => {
        on = !on
        const t = $(".txt", r)
        t.textContent = on ? en : es
        t.className = on ? "txt ai-box fade" : "txt"
        $(".tg", r).textContent = on ? "Show original" : "Show translation"
        $(".info", r).textContent = on ? "Translated from Spanish" : ""
      }
    },
  },

  conf: {
    html: `<div class="d"><div class="panel col" style="gap:0">
      ${[
        ["Vendor", "Acme Inc.", 1],
        ["Amount", "$1,240.00", 1],
        ["Due date", "Mar 3?", 0],
      ]
        .map(
          ([k, v, ok]) =>
            `<div class="row" style="justify-content:space-between;padding:8px 0;border-bottom:1px solid var(--line)"><span class="lbl" style="width:60px">${k}</span><span class="tint" style="flex:1">${v}</span><span style="font-size:11px;color:${ok ? "var(--ok)" : "var(--warn)"};${ok ? "" : "background:var(--warn-tint);border-radius:999px;padding:1px 8px"}">${ok ? "● Sure" : "● Check this"}</span></div>`
        )
        .join("")}
      <div class="lbl" style="padding-top:8px">The date was smudged on the scan.</div></div></div>`,
  },

  diff: {
    try: true,
    html: `<div class="d col"><div class="panel tx" style="font-size:14px">We <del style="color:var(--bad)">will be able to</del> <ins style="text-decoration:none" class="tint">can</ins> ship the update <del style="color:var(--bad)">in the near future</del><ins style="text-decoration:none" class="tint"> next week</ins>.</div>
      <div class="acts ac"><button class="btn primary y">Accept all</button><button class="btn n">Reject</button></div></div>`,
    init(r) {
      $(".y", r).onclick = () => {
        $$("del", r).forEach((d) => d.remove())
        $(".ac", r).innerHTML =
          '<span class="check">✓ Changes applied</span> <button class="mini">Undo</button>'
      }
      $(".n", r).onclick = () => {
        $$("ins", r).forEach((d) => d.remove())
        $$("del", r).forEach((d) => {
          d.style.color = ""
          d.style.textDecoration = "none"
        })
        $(".ac", r).innerHTML = '<span class="lbl">Kept your original</span>'
      }
    },
  },

  why: {
    try: true,
    html: `<div class="d col"><div class="panel">
      <div class="lbl">Recommended</div><div style="font-weight:600;margin:2px 0 6px">Design critique template</div>
      <button class="mini w">Why this?</button><div class="ex"></div></div></div>`,
    init(r) {
      $(".w", r).onclick = () => {
        $(".ex", r).innerHTML =
          '<div class="ai-box fade" style="margin-top:8px">You opened 4 critique docs this month.<div style="margin-top:6px"><button class="mini">Show fewer like this</button></div></div>'
      }
    },
  },

  undo: {
    try: true,
    html: `<div class="d col" style="height:170px">
      ${["Receipts", "Newsletters", "Team"]
        .map(
          (t, i) =>
            `<div class="row" style="justify-content:space-between;font-size:12px"><span>📁 ${t}</span><span class="lbl n">${[9, 11, 4][i]}</span></div>`
        )
        .join("")}
      <div class="toast" style="margin-top:auto;background:var(--ink);color:#fff;border-radius:10px;padding:9px 12px;display:flex;justify-content:space-between;align-items:center;visibility:hidden"><span class="m">Sorted 24 emails into 3 folders</span><button class="u" style="color:#b9a8ff;font-weight:600">Undo</button></div></div>`,
    async run(r) {
      await wait(500)
      if (!r.isConnected) return
      const t = $(".toast", r)
      t.style.visibility = "visible"
      t.classList.add("fade")
    },
    init(r) {
      $(".u", r).onclick = () => {
        $$(".n", r).forEach((n) => (n.textContent = "0"))
        $(".m", r).textContent = "Moved 24 emails back to Inbox"
        $(".u", r).remove()
      }
    },
  },

  /* ------------------------------------------------------- NEW (for review) */

  // NEW — Mixed input: compose one request from several modalities.
  mixed: {
    try: true,
    html: `<div class="d col"><div class="lbl">Ask in any mix of ways</div>
      <div class="field comp" style="min-height:60px"><span class="ph">Type, talk, or drop an image…</span></div>
      <div class="acts" style="justify-content:center">
        <button class="chip" data-add="type">⌨ Type</button>
        <button class="chip" data-add="talk">🎤 Talk</button>
        <button class="chip" data-add="image">🖼 Image</button></div></div>`,
    init(r) {
      const comp = $(".comp", r)
      const adds: Record<string, string> = {
        type: "Summarize this thread",
        talk: '<span class="said">“and flag anything urgent”</span>',
        image: '<span class="tint" style="padding:1px 6px">🖼 roadmap.png</span>',
      }
      $$("[data-add]", r).forEach(
        (b) =>
          (b.onclick = () => {
            const ph = comp.querySelector(".ph")
            if (ph) ph.remove()
            const key = b.dataset.add ?? "type"
            const frag = document.createElement("span")
            frag.className = "fade"
            frag.innerHTML = (comp.textContent?.trim() ? " " : "") + adds[key]
            comp.appendChild(frag)
          })
      )
    },
  },

  // NEW — Read-aloud: the assistant speaks its reply. A waveform pulses while the
  // words appear in time, as if you were hearing it read out.
  readaloud: {
    html: `<div class="d col" style="align-items:center">
      <div class="wave" aria-hidden="true">${Array.from(
        { length: 9 },
        (_, i) => `<span class="bar" style="animation-delay:${(i * 0.08).toFixed(2)}s"></span>`
      ).join("")}</div>
      <div class="ai-box rd" style="line-height:1.6;min-height:46px;align-self:stretch"></div>
      <div class="lbl st">Speaking…</div></div>`,
    async run(r) {
      const words = "Your order shipped today and should arrive by Thursday.".split(" ")
      const box = $(".rd", r)
      await wait(350)
      for (let i = 0; i < words.length; i++) {
        if (!r.isConnected) return
        box.innerHTML = words
          .slice(0, i + 1)
          .map((w, j) => `<span class="wd${j === i ? " spk" : ""}">${w}</span>`)
          .join(" ")
        await wait(300)
      }
      if (!r.isConnected) return
      box.innerHTML = words.map((w) => `<span class="wd">${w}</span>`).join(" ")
      $(".st", r).textContent = "Done"
      $(".wave", r).classList.add("stopped")
    },
  },

  // NEW — Ongoing monitoring: watch a metric and alert when it crosses a line.
  monitoring: {
    html: `<div class="d col"><div class="panel">
      <div class="row" style="justify-content:space-between"><span class="lbl">Error rate</span><span class="lbl watch">● Watching</span></div>
      <svg viewBox="0 0 280 80" width="100%" aria-hidden="true"><line class="thr" x1="0" y1="34" x2="280" y2="34" stroke="var(--line)" stroke-width="1.5" stroke-dasharray="4 4"/><polyline class="ln" fill="none" stroke="var(--ink)" stroke-width="2" stroke-linejoin="round"/><circle class="dot" r="6" fill="var(--warn)" opacity="0"/></svg></div>
      <div class="ai-box co" style="visibility:hidden">Error rate crossed the line. Paged the on-call engineer.</div></div>`,
    async run(r) {
      const v = [60, 58, 61, 57, 59, 55, 40, 28, 20]
      const pts = v.map((y, i) => [10 + i * 32, y])
      const ln = $<SVGPolylineElement>(".ln", r)
      ln.setAttribute("points", pts.map((p) => p.join(",")).join(" "))
      const len = ln.getTotalLength ? ln.getTotalLength() : 600
      ln.style.strokeDasharray = String(len)
      ln.style.strokeDashoffset = String(len)
      ln.style.transition = RM ? "none" : "stroke-dashoffset 1.1s ease"
      await wait(50)
      ln.style.strokeDashoffset = "0"
      await wait(1100)
      if (!r.isConnected) return
      const d = $<SVGCircleElement>(".dot", r)
      const cross = pts[7]
      d.setAttribute("cx", String(cross[0]))
      d.setAttribute("cy", String(cross[1]))
      d.setAttribute("opacity", "1")
      await wait(300)
      const c = $(".co", r)
      c.style.visibility = "visible"
      c.classList.add("fade")
    },
  },

  // NEW — Review plan before running: show every step, run only on approval.
  planreview: {
    try: true,
    html: `<div class="d"><div class="panel col">
      <div style="font-weight:600">Plan · archive stale projects</div>
      ${["Find projects idle 90+ days", "Export each to cold storage", "Mark them archived"]
        .map(
          (t) =>
            `<div class="row st" style="color:var(--muted)"><span class="ic" style="width:16px;text-align:center">○</span>${t}</div>`
        )
        .join("")}
      <div class="acts out"><button class="btn">Edit plan</button><button class="btn primary go">Run plan</button></div></div></div>`,
    init(r) {
      $(".go", r).onclick = async () => {
        const out = $(".out", r)
        out.innerHTML = '<span class="lbl">Running…</span>'
        for (const s of $$(".st", r)) {
          if (!r.isConnected) return
          s.style.color = "var(--ink)"
          const ic = $(".ic", s)
          ic.textContent = "◌"
          ic.style.color = "var(--ai)"
          await wait(700)
          if (!r.isConnected) return
          ic.textContent = "✓"
          ic.style.color = "var(--ok)"
        }
        if (r.isConnected) out.innerHTML = '<span class="check fade">✓ Plan complete</span>'
      }
    },
  },

  // NEW — Stated assumptions: reveal what the answer took for granted.
  assumptions: {
    try: true,
    html: `<div class="d col">
      <div class="ai-box">At about 20 orders a day, you'll reach the plan limit in roughly 6 weeks.</div>
      <button class="mini as">Show assumptions</button>
      <ul class="asl ai-box" style="display:none;margin:0;padding:6px 10px 6px 24px;font-size:13px"></ul></div>`,
    init(r) {
      const items = [
        "Current rate holds at ~20 orders/day",
        "No seasonal spike before then",
        "Plan limit is 800 orders/month",
      ]
      $(".as", r).onclick = () => {
        const ul = $(".asl", r)
        const open = ul.style.display !== "none"
        if (open) {
          ul.style.display = "none"
          ul.innerHTML = ""
          $(".as", r).textContent = "Show assumptions"
        } else {
          ul.style.display = "block"
          ul.innerHTML = items.map((t) => `<li class="fade">${t}</li>`).join("")
          $(".as", r).textContent = "Hide assumptions"
        }
      }
    },
  },
}
